import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../api/client';
import { useCanWriteCompanySettings } from '../../hooks/useCanWriteCompanySettings';
import { toast } from 'sonner';
import TruckingPositionsSection from './TruckingPositionsSection';

/*
 * Only the three module switches, because they are the only settings on this
 * page anything reads: the trucking sidebar (HomeScreen) drops Bills of Lading,
 * Weight Tickets and Move Jobs when they are off.
 *
 * PATCH /settings/trucking also stores bol_prefix and weight_ticket_prefix
 * (numbering hardcodes WT- and a bare YYYY-NNNN), default_driver_pay_type and
 * default_driver_pay_rate, nsc_number, ifta_number, wcb_number,
 * provincial_carrier_number and auto_invoice_on_bol_approval. They save, and
 * nothing reads any of them back. They are left off this page rather than
 * shown as settings that do nothing, and the PATCH carries only the keys an
 * edit touched, so a save never writes them. Wire a reader up before bringing
 * one back.
 *
 * The four trucking settings that ARE live — bol_email_driver,
 * bol_email_customer, bol_custom_footer, bol_company_logo_url — have no
 * control on this page at all. That gap is known and not closed here.
 */
type ModuleKey = 'weight_tickets_enabled' | 'bol_enabled' | 'move_jobs_enabled';

const MODULES: { key: ModuleKey; label: string; desc: string }[] = [
  { key: 'weight_tickets_enabled', label: 'Weight Tickets', desc: 'Shows Weight Tickets in the trucking menu.' },
  { key: 'bol_enabled', label: 'Bills of Lading', desc: 'Shows Bills of Lading in the trucking menu.' },
  { key: 'move_jobs_enabled', label: 'Move Job Management', desc: 'Shows Move Jobs in the trucking menu.' },
];

export default function TruckingSettingsPage() {
  const { hasVertical } = useAuth();

  if (!hasVertical('trucking')) {
    return (
      <div className="min-h-full">
        <div className="px-6 py-12 text-center text-fieldiq-muted">Trucking vertical is not enabled for this company.</div>
      </div>
    );
  }

  return <TruckingSettingsInner />;
}

function TruckingSettingsInner() {
  const queryClient = useQueryClient();
  // PATCH /settings/trucking is requireRole('admin'), non-strict.
  const canWrite = useCanWriteCompanySettings();

  // Same key and shape as the sidebar's read (HomeScreen, useTruckingFeature),
  // so a save here updates the menu without a reload.
  const { data, isLoading } = useQuery({
    queryKey: ['settings', 'trucking'],
    queryFn: () => apiClient.get('/settings/trucking').then(r => r.data?.data ?? r.data ?? {}),
  });

  const [form, setForm] = useState<Record<ModuleKey, boolean>>({
    weight_tickets_enabled: true,
    bol_enabled: true,
    move_jobs_enabled: true,
  });
  /** Exactly which keys this edit touched. The PATCH carries these and only these. */
  const [dirtyKeys, setDirtyKeys] = useState<Set<ModuleKey>>(new Set());

  useEffect(() => {
    if (data) {
      setForm(prev => ({
        weight_tickets_enabled: data.weight_tickets_enabled ?? prev.weight_tickets_enabled,
        bol_enabled: data.bol_enabled ?? prev.bol_enabled,
        move_jobs_enabled: data.move_jobs_enabled ?? prev.move_jobs_enabled,
      }));
      setDirtyKeys(new Set());
    }
  }, [data]);

  const toggle = (key: ModuleKey) => {
    setForm(prev => ({ ...prev, [key]: !prev[key] }));
    setDirtyKeys(prev => new Set(prev).add(key));
  };

  const dirty = dirtyKeys.size > 0;

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload: Partial<Record<ModuleKey, boolean>> = {};
      dirtyKeys.forEach(k => { payload[k] = form[k]; });
      return apiClient.patch('/settings/trucking', payload);
    },
    onSuccess: () => {
      // Shared with the sidebar's read, so the menu follows the save.
      queryClient.invalidateQueries({ queryKey: ['settings', 'trucking'] });
      setDirtyKeys(new Set());
      toast.success('Trucking settings saved');
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to save'),
  });

  if (isLoading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-fieldiq-accent animate-spin" /></div>;
  }

  return (
    <div className="min-h-full">
      {/* The Operations workspace renders the title and the tab strip. */}
      <div className="px-6 py-4 space-y-6 max-w-2xl">
        {canWrite ? (
          <div className="flex justify-end">
            <button
              onClick={() => saveMutation.mutate()}
              disabled={!dirty || saveMutation.isPending}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                dirty ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'bg-fieldiq-border text-fieldiq-muted cursor-not-allowed'
              }`}
            >
              {saveMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : null}
              {saveMutation.isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        ) : (
          <p className="text-xs text-fieldiq-muted">Only an admin can change trucking settings.</p>
        )}

        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-white">Features</h3>
          {MODULES.map(m => (
            <div key={m.key} className="flex items-center justify-between bg-fieldiq-card border border-fieldiq-border rounded-xl p-4">
              <div className="flex-1 mr-4">
                <p className="text-sm font-medium text-white">{m.label}</p>
                <p className="text-xs text-fieldiq-muted mt-0.5">{m.desc}</p>
              </div>
              <button onClick={() => toggle(m.key)} disabled={!canWrite}
                role="switch" aria-checked={form[m.key]} aria-label={m.label}
                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors shrink-0 disabled:cursor-not-allowed disabled:opacity-60 ${form[m.key] ? 'bg-fieldiq-accent' : 'bg-fieldiq-border'}`}>
                <span className={`inline-block h-5 w-5 rounded-full bg-white transition-transform ${form[m.key] ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
          ))}
        </div>

        {/* Moved here from People & access: trucking data, trucking tenants only.
          * Row-level saves, so the Save button above does not cover it. */}
        <TruckingPositionsSection canWrite={canWrite} />
      </div>
    </div>
  );
}
